import { FC, useCallback, useEffect, useState } from 'react';
import {
  ComboBox,
  InlineNotification,
  Layer,
  Modal,
  TextInput,
} from '@carbon/react';
import { useAtomValue, useSetAtom } from 'jotai';

import { WellsAutocomplete } from 'components/wells-autocomplete';
import type { WellsAutocompleteRow } from 'types/api-responses';

import { AssetEntityField } from './asset-entity-field';
import {
  createWellboreAssetAtom,
  createWellboreAssetLoadableAtom,
} from './atoms';
import type { EntityRef } from './types';

import styles from './add-wellbore-modal.module.scss';

/**
 * Carbon enum-source for the instrument-type dropdown — matches
 * `InterestsCalculator::Constants::INTERESTS_BY_INSTRUMENT_TYPE.keys`
 * exactly. Hard-coded rather than fetched because the list is small +
 * stable; the BE validator rejects any value not in this set.
 */
const INSTRUMENT_TYPE_OPTIONS = ['MD', 'NPRI', 'OGL', 'ORRI', 'UMI'] as const;
type InstrumentType = (typeof INSTRUMENT_TYPE_OPTIONS)[number];

/** Props for {@link AddWellboreModal}. */
export interface AddWellboreModalProps {
  /** Whether the modal is visible — parent owns the open state. */
  open: boolean;
  /** Close handler — fires on cancel, X click, or post-submit success. */
  onClose: () => void;
  /**
   * Entities the asset may be filed under — the sellers behind the wizard's
   * Step 0 selection. One is filled in; several are offered as a choice; none
   * leaves the Create button disabled with the reason on screen.
   */
  entityOptions: EntityRef[];
  /** UUID of the user's current draft. When set, the BE attaches via PackageAsset. */
  draftId: string | null;
  /**
   * Called after a successful POST so the parent can refetch slot / asset
   * views. Errors stay on the loadable and surface as the modal banner;
   * the parent isn't notified.
   */
  onSuccess?: () => void;
}

/**
 * Modal opened from the AssetsTable's "Add Wellbore" toolbar button
 * (INS-9038 wires the trigger; INS-9039 builds the modal). UWI search
 * (shared {@link WellsAutocomplete}) drives the read-only Well Name + Unit
 * fields; user fills NRI / WI / Property Number; submit fires
 * `POST /entities/:entity_id/assets` with `kind: 'wellbore'`. The BE
 * returns 201 and the new asset is immediately attached to the draft via
 * `package_draft_id` — no materialization wait needed (the
 * `Query::Packages::DraftAssets` non-tract leg reads
 * `Asset::Base.where(package_draft_id: draft.id)` directly).
 *
 * canAdd gating: UWI selected + non-empty instrument_type + valid NRI in
 * [0, 1] + valid WI in [0, 1] + no submit in flight.
 *
 * Reset-on-close: every transition out of `open: true` clears the form so
 * the next open lands fresh.
 *
 * @param {AddWellboreModalProps} props - Modal open / close / target / draft
 * @return {JSX.Element} The Add Wellbore modal
 */
export const AddWellboreModal: FC<AddWellboreModalProps> = ({
  open,
  onClose,
  entityOptions,
  draftId,
  onSuccess,
}) => {
  const createAsset = useSetAtom(createWellboreAssetAtom);
  const createState = useAtomValue(createWellboreAssetLoadableAtom);
  const isSubmitting = createState.state === 'loading';

  const [entityId, setEntityId] = useState<number | null>(null);
  const [selectedWell, setSelectedWell] = useState<WellsAutocompleteRow | null>(
    null
  );
  const [instrumentType, setInstrumentType] = useState<InstrumentType | ''>('');
  const [nri, setNri] = useState<string>('');
  const [wi, setWi] = useState<string>('');
  const [propertyNumber, setPropertyNumber] = useState<string>('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const resetForm = useCallback(() => {
    setEntityId(null);
    setSelectedWell(null);
    setInstrumentType('');
    setNri('');
    setWi('');
    setPropertyNumber('');
    setSubmitAttempted(false);
  }, []);

  // Reset every time the modal closes — covers cancel, X, and post-success.
  useEffect(() => {
    if (!open) resetForm();
  }, [open, resetForm]);

  // Reading the choice back out of the options rather than holding the entity
  // itself keeps a stale pick from surviving a Step 0 edit that dropped it.
  const entity =
    entityOptions.length === 1
      ? entityOptions[0]
      : (entityOptions.find((option) => option.id === entityId) ?? null);

  const wiEnabled = instrumentType === 'OGL';

  const nriNum = nri === '' ? null : Number(nri);
  const wiNum = wi === '' ? null : Number(wi);
  const nriValid =
    nriNum !== null && Number.isFinite(nriNum) && nriNum >= 0 && nriNum <= 1;
  const wiValid =
    wiNum !== null && Number.isFinite(wiNum) && wiNum >= 0 && wiNum <= 1;

  const canAdd =
    entity !== null &&
    selectedWell !== null &&
    instrumentType !== '' &&
    nriValid &&
    (!wiEnabled || wiValid) &&
    !isSubmitting;

  const handleSubmit = useCallback(async () => {
    if (
      !canAdd ||
      !entity ||
      !selectedWell ||
      !instrumentType ||
      nriNum === null
    ) {
      return;
    }
    setSubmitAttempted(true);
    try {
      await createAsset({
        entityId: entity.id,
        draftId: draftId ?? undefined,
        asset: {
          kind: 'wellbore',
          uwi: selectedWell.uwi,
          instrumentType,
          nri: nriNum,
          wi: wiEnabled ? wiNum : null,
          propertyNumber: propertyNumber || null,
        },
      });
      onSuccess?.();
      onClose();
    } catch {
      // Error surfaces on the loadable / banner.
    }
  }, [
    canAdd,
    createAsset,
    draftId,
    entity,
    instrumentType,
    nriNum,
    onClose,
    onSuccess,
    propertyNumber,
    selectedWell,
    wiEnabled,
    wiNum,
  ]);

  // Scoped to this opening of the modal: the create atom is shared and holds
  // its last error, so without the flag a failed submit's banner greets the
  // next opening as though the new attempt had failed.
  const errorMessage =
    submitAttempted && createState.state === 'hasError'
      ? createState.error instanceof Error
        ? createState.error.message
        : 'Failed to add wellbore asset'
      : null;

  // Validation visibility: render `invalid` only after the user has
  // typed something. An empty NumberInput on first render shouldn't
  // flash the red 0–1 error message (that's the mockUI's clean state).
  const showNriError = nri !== '' && !nriValid;
  const showWiError = wiEnabled && wi !== '' && !wiValid;

  return (
    <Modal
      modalHeading="Create Wellbore Asset"
      modalLabel=""
      open={open}
      primaryButtonText={isSubmitting ? 'Creating…' : 'Create and Add'}
      primaryButtonDisabled={!canAdd}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleSubmit}
      onRequestClose={onClose}
      preventCloseOnClickOutside
    >
      <div className={styles.entityRow}>
        <AssetEntityField
          id="add-wellbore-entity"
          options={entityOptions}
          value={entity}
          onChange={setEntityId}
          disabled={isSubmitting}
        />
      </div>
      <p className={styles.subtitle}>
        Please fill out the following fields and click &ldquo;Create and
        Add&rdquo; when you are done.
      </p>
      <div className={styles.formGrid}>
        <WellsAutocomplete
          light
          id="add-wellbore-uwi"
          titleText="Wellbore *"
          placeholder="Search by UWI or well name…"
          helperText="Only wells with an active unit assignment are shown"
          selectedItem={selectedWell}
          onChange={setSelectedWell}
          disabled={isSubmitting}
        />
        <Layer>
          <TextInput
            id="add-wellbore-well-name"
            labelText="Well Name"
            value={selectedWell?.name ?? ''}
            readOnly
            placeholder="—"
          />
        </Layer>
        <Layer>
          <ComboBox<InstrumentType>
            id="add-wellbore-instrument-type"
            titleText="Instrument Type *"
            placeholder="Choose an option"
            items={[...INSTRUMENT_TYPE_OPTIONS]}
            itemToString={(t) => t || ''}
            selectedItem={instrumentType || null}
            onChange={({ selectedItem }) => {
              const newType = selectedItem ?? '';
              setInstrumentType(newType);
              if (newType !== 'OGL') setWi('');
            }}
            disabled={isSubmitting}
          />
        </Layer>
        <Layer>
          <TextInput
            id="add-wellbore-unit"
            labelText="Unit"
            value={selectedWell?.unitName ?? ''}
            readOnly
            placeholder="—"
          />
        </Layer>
        <Layer>
          <TextInput
            id="add-wellbore-nri"
            labelText="Net Revenue Interest *"
            helperText="0–1, e.g. 0.16667"
            value={nri}
            onChange={(e) => setNri(e.target.value)}
            invalid={showNriError}
            invalidText="NRI must be between 0 and 1"
            disabled={isSubmitting}
          />
        </Layer>
        <Layer>
          <TextInput
            id="add-wellbore-wi"
            labelText={wiEnabled ? 'Working Interest *' : 'Working Interest'}
            helperText="0–1, e.g. 0.5"
            value={wi}
            onChange={(e) => setWi(e.target.value)}
            invalid={showWiError}
            invalidText="WI must be between 0 and 1"
            disabled={!wiEnabled || isSubmitting}
          />
        </Layer>
        <div className={styles.fullRow}>
          <Layer>
            <TextInput
              id="add-wellbore-property-number"
              labelText="Property Number (optional)"
              value={propertyNumber}
              onChange={(e) => setPropertyNumber(e.target.value)}
              disabled={isSubmitting}
            />
          </Layer>
        </div>
        {errorMessage && (
          <div className={styles.fullRow}>
            <InlineNotification
              kind="error"
              title="Failed to add wellbore"
              subtitle={errorMessage}
              hideCloseButton
              lowContrast
            />
          </div>
        )}
      </div>
    </Modal>
  );
};
